import { useState } from "react";
import { Box, Stack, IconButton, Tooltip } from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { Text, COLORS, ButtonV2 } from "@asayinc/component-library";

import { Card } from "src/components/atoms/Card";
import { LoadingCard } from "src/components/molecules/Loading";
import { useUsersQuery } from "src/store/user/api";
import { User } from "src/store/user/types";
import { CreateUpdateUserDialog } from "src/pages/Settings/components/CreateUpdateUserDialog";
import { DeleteUserDialog } from "src/pages/Settings/components/DeleteUserDialog";

export function UserManagement() {
  const { data: users, isLoading } = useUsersQuery();

  const [createEditDialogOpen, setCreateEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);

  const handleCreateClick = () => {
    setEditingUser(null);
    setCreateEditDialogOpen(true);
  };

  const handleEditClick = (user: User) => {
    setEditingUser(user);
    setCreateEditDialogOpen(true);
  };

  const handleDeleteClick = (user: User) => {
    setDeletingUser(user);
    setDeleteDialogOpen(true);
  };

  const handleCreateEditDialogClose = () => {
    setCreateEditDialogOpen(false);
    setEditingUser(null);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setDeletingUser(null);
  };

  if (isLoading) {
    return <LoadingCard />;
  }

  return (
    <>
      <Card title="User Management" sx={{ mb: 6 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Text variant="body3" sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}>
            Manage users who have access to the intermediary portal in your
            organization. Only super admins can manage users. Super admins and
            admins can view and access protected features such as sending
            reports.
          </Text>
          <ButtonV2
            variant="primary"
            size="small"
            startIcon={<Add />}
            onClick={handleCreateClick}
          >
            Add User
          </ButtonV2>
        </Box>

        {users && users.length > 0 ? (
          <Stack spacing={2}>
            {users.map((user) => (
              <Box
                key={user.id}
                sx={{
                  p: 3,
                  borderRadius: 1,
                  border: `1px solid ${COLORS.P_OUTLINE}`,
                  backgroundColor: COLORS.WHITE,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Text variant="body2" sx={{ fontWeight: 500 }}>
                    {user.firstName} {user.lastName}
                  </Text>
                  <Text
                    variant="body3"
                    sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}
                  >
                    {user.email}
                  </Text>
                  {user.isIntermediaryPortalAdmin && (
                    <Text
                      variant="body3"
                      sx={{
                        color: "#1968FF",
                        fontWeight: 500,
                        mt: 0.5,
                      }}
                    >
                      Admin
                    </Text>
                  )}
                  {/* Associated Companies */}
                  {(user.solicitors?.length > 0 ||
                    user.tabulators?.length > 0 ||
                    user.claimsAdministrators?.length > 0) && (
                    <Box sx={{ mt: 1 }}>
                      {user.solicitors?.length > 0 && (
                        <Text
                          variant="body3"
                          sx={{
                            color: COLORS.P_MED_LOW_EMPHASIS,
                            display: "block",
                          }}
                        >
                          Solicitors:{" "}
                          {user.solicitors.map((s) => s.name).join(", ")}
                        </Text>
                      )}
                      {user.tabulators?.length > 0 && (
                        <Text
                          variant="body3"
                          sx={{
                            color: COLORS.P_MED_LOW_EMPHASIS,
                            display: "block",
                          }}
                        >
                          Tabulators:{" "}
                          {user.tabulators.map((t) => t.name).join(", ")}
                        </Text>
                      )}
                      {user.claimsAdministrators?.length > 0 && (
                        <Text
                          variant="body3"
                          sx={{
                            color: COLORS.P_MED_LOW_EMPHASIS,
                            display: "block",
                          }}
                        >
                          Claims Administrators:{" "}
                          {user.claimsAdministrators
                            .map((c) => c.name)
                            .join(", ")}
                        </Text>
                      )}
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title="Edit user">
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(user)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete user">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(user)}
                      sx={{ color: COLORS.SATURATED_RED }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Stack>
        ) : (
          <Text variant="body2" sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}>
            No users found.
          </Text>
        )}
      </Card>

      <CreateUpdateUserDialog
        open={createEditDialogOpen}
        onClose={handleCreateEditDialogClose}
        user={editingUser}
      />

      <DeleteUserDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        user={deletingUser}
      />
    </>
  );
}
